/**
 * Notification Toast Component
 * 
 * Renders notifications from the UI store with auto-dismiss and animations.
 */

import { X, CheckCircle, AlertCircle, AlertTriangle, Info } from 'lucide-react';
import { useUIStore } from '../stores/prioritization/uiStore';

export function NotificationToast() {
  const notifications = useUIStore(state => state.notifications);
  const removeNotification = useUIStore(state => state.removeNotification);
  
  if (notifications.length === 0) {
    return null;
  }
  
  const getIcon = (type: 'info' | 'success' | 'warning' | 'error') => {
    switch (type) {
      case 'success':
        return <CheckCircle className="h-5 w-5 text-[var(--green)]" />;
      case 'error':
        return <AlertCircle className="h-5 w-5 text-[var(--coral)]" />;
      case 'warning':
        return <AlertTriangle className="h-5 w-5 text-[var(--mustard)]" />;
      default:
        return <Info className="h-5 w-5 text-[var(--text-ter)]" />;
    }
  };
  
  const getContainerClass = (type: 'info' | 'success' | 'warning' | 'error') => {
    switch (type) {
      case 'success':
        return 'bg-[var(--green-dim)] border-[var(--green)]';
      case 'error':
        return 'bg-[var(--coral-dim)] border-[var(--coral)]';
      case 'warning':
        return 'bg-[var(--mustard-dim)] border-[var(--mustard)]';
      default:
        return 'bg-[var(--slate-dim)] border-[var(--slate)]';
    }
  };
  
  const getTitleClass = (type: 'info' | 'success' | 'warning' | 'error') => {
    switch (type) {
      case 'success':
        return 'text-[var(--green)] dark:text-[var(--text-ter)]';
      case 'error':
        return 'text-[var(--coral)]';
      case 'warning':
        return 'text-[var(--mustard)]';
      default:
        return 'text-[var(--text-ter)]';
    }
  };
  
  const getMessageClass = (type: 'info' | 'success' | 'warning' | 'error') => {
    switch (type) {
      case 'success':
        return 'text-[var(--green)] dark:text-[var(--text-ter)]';
      case 'error':
        return 'text-[var(--coral)]';
      case 'warning':
        return 'text-[var(--mustard)]';
      default:
        return 'text-[var(--text-ter)]';
    }
  };
  
  return (
    <div className="fixed top-4 right-4 z-[100] flex flex-col gap-3 max-w-md w-full pointer-events-none">
      {notifications.map((notification) => (
        <div
          key={notification.id}
          className={`
            pointer-events-auto
            flex items-start gap-3 p-4  border shadow-lg
            animate-in slide-in-from-right-5 fade-in duration-300
            ${getContainerClass(notification.type)}
          `}
        >
          <div className="flex-shrink-0 mt-0.5">
            {getIcon(notification.type)}
          </div>
          
          <div className="flex-1 min-w-0">
            <p className={`text-sm font-semibold ${getTitleClass(notification.type)}`}>
              {notification.title}
            </p>
            <p className={`text-sm mt-1 ${getMessageClass(notification.type)}`}>
              {notification.message}
            </p>
          </div>
          
          <button
            onClick={() => removeNotification(notification.id)}
            className="flex-shrink-0 p-1 rounded hover:bg-black/5 transition-colors"
            aria-label="Dismiss notification"
          >
            <X className="h-4 w-4 text-[var(--text-ter)]" />
          </button>
        </div>
      ))}
    </div>
  );
}

export default NotificationToast;


